import React, { useEffect, useState, Fragment } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { User, Users, UserRound, UsersRound, Link2, Globe, CheckCircle2, Network, ShieldCheck, Zap, Sparkles } from 'lucide-react';
type Audience = 'personal' | 'team';
// The swappable orange ending of "Giga does the work you ___". Each audience
// rotates through a few phrasings so the line feels alive rather than static.
const ENDING_PHRASES: Record<Audience, string[]> = {
  personal: ["shouldn't be doing yourself.", "can't get to.", 'keep putting off.', "don't have time for."],
  team: ["can't justify hiring for.", "can't keep up with.", 'keeps piling up.', "shouldn't pay people to do."]
};
function RotatingPhrase({
  phrases
}: {
  phrases: string[];
}) {
  const reduceMotion = useReducedMotion();
  const [i, setI] = useState(0);
  useEffect(() => {
    if (reduceMotion || phrases.length <= 1) return;
    const id = window.setInterval(() => {
      setI(p => (p + 1) % phrases.length);
    }, 2800);
    return () => clearInterval(id);
  }, [phrases, reduceMotion]);
  if (reduceMotion) {
    return <span className="text-[#EA580C]" data-id="element-341">{phrases[0]}</span>;
  }
  return <span className="relative inline-block align-bottom text-[#EA580C]" data-id="element-342">
      {/* Invisible sizer keeps the layout from jumping as phrases change. */}
      <span aria-hidden="true" className="invisible whitespace-pre-wrap" data-id="element-343">
        {phrases.reduce((a, b) => a.length >= b.length ? a : b)}
      </span>
      <span className="absolute inset-0" data-id="element-344">
        <AnimatePresence mode="wait" data-id="element-345">
          <motion.span key={i} initial={{
          opacity: 0,
          y: '0.5em'
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: '-0.5em'
        }} transition={{
          duration: 0.4,
          ease: [0.16, 1, 0.3, 1]
        }} className="absolute inset-0" data-id="element-346">
            {phrases[i]}
          </motion.span>
        </AnimatePresence>
      </span>
    </span>;
}
export function AudienceSwitch() {
  const [audience, setAudience] = useState<Audience>('personal');
  // Auto-advance between the two audiences so passive visitors see both
  // stories. Pauses on hover/focus and once the user picks a tab themselves.
  const [paused, setPaused] = useState(false);
  const reduceMotion = useReducedMotion();
  useEffect(() => {
    if (paused || reduceMotion) return;
    const id = window.setInterval(() => {
      setAudience(a => a === 'personal' ? 'team' : 'personal');
    }, 5200);
    return () => clearInterval(id);
  }, [paused, reduceMotion]);
  // A manual pick stops the carousel for the rest of the visit.
  const pick = (a: Audience) => {
    setPaused(true);
    setAudience(a);
  };
  return <section onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} className="w-full bg-[#FAF8F4] py-24 md:py-40 border-b border-neutral-200/70 relative overflow-hidden" data-id="element-347">
      {/* Grand atmospheric backdrop — a warm glow rising from the top center
                    plus a faint architectural grid that fades out, so the section reads
                    as a marquee moment rather than another plain band. The glow's
                    horizontal position tracks the selected audience, so flipping the
                    toggle visibly re-lights the whole stage. */}
      <motion.div aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-0 h-[640px]" animate={{
      background: audience === 'personal' ? 'radial-gradient(70% 100% at 38% -10%, rgba(234,88,12,0.16), transparent 60%)' : 'radial-gradient(70% 100% at 62% -10%, rgba(234,88,12,0.16), transparent 60%)'
    }} transition={{
      duration: 0.8,
      ease: 'easeOut'
    }} data-id="element-348" />
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-[0.5] [mask-image:radial-gradient(70%_55%_at_50%_18%,#000,transparent_75%)] bg-[linear-gradient(to_right,rgba(43,33,26,0.05)_1px,transparent_1px),linear-gradient(to_bottom,rgba(43,33,26,0.05)_1px,transparent_1px)] bg-[size:64px_64px]" data-id="element-349" />

      <div className="max-w-[1280px] mx-auto px-6 md:px-10 relative" data-id="element-350">
        {/* Section opening — scaled up into a marquee header. */}
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-80px'
      }} transition={{
        duration: 0.6,
        ease: [0.16, 1, 0.3, 1]
      }} className="mb-10 md:mb-12 max-w-4xl mx-auto text-center" data-id="element-351">
          <span className="inline-flex items-center gap-2 rounded-full border border-neutral-200/80 bg-white/70 px-4 py-1.5 backdrop-blur-sm text-[11px] font-semibold tracking-[4px] uppercase text-neutral-500 shadow-sm mb-7" data-id="element-352">
            <span className="h-1.5 w-1.5 rounded-full bg-[#EA580C]" data-id="element-353" />
            Built for how you work
          </span>
          <h2 className="text-5xl md:text-7xl lg:text-[5.5rem] font-title font-medium text-[#2B211A] tracking-tight leading-[0.98] mb-6" data-id="element-354">
            One Giga.
            <br data-id="element-355" />
            <span className="italic font-light text-[#EA580C]" data-id="element-356">
              Two ways
            </span>{' '}
            to put it to work.
          </h2>
          <p className="text-lg md:text-xl text-neutral-500 leading-relaxed max-w-2xl mx-auto" data-id="element-357">
            Whether it's just you or your whole company, Giga shapes itself
            around the way you already operate.
          </p>
        </motion.div>

        {/* Toggle */}
        <motion.div initial={{
        opacity: 0,
        y: 12
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-80px'
      }} transition={{
        duration: 0.55,
        delay: 0.12,
        ease: [0.16, 1, 0.3, 1]
      }} className="relative flex flex-col items-center mb-16 md:mb-24" data-id="element-358">
          {/* Reactive spotlight pooled beneath the toggle — it slides toward
           the active side and pulses softly, putting the control on a stage. */}
          <motion.div aria-hidden="true" className="pointer-events-none absolute top-1/2 h-44 w-[440px] max-w-[90vw] -translate-y-1/2 rounded-full blur-3xl" style={{
          background: 'radial-gradient(circle, rgba(234,88,12,0.28), transparent 70%)'
        }} animate={{
          x: audience === 'personal' ? -70 : 70,
          opacity: [0.7, 0.95, 0.7]
        }} transition={{
          x: {
            type: 'spring',
            stiffness: 220,
            damping: 30
          },
          opacity: {
            duration: 4,
            repeat: Infinity,
            ease: 'easeInOut'
          }
        }} data-id="element-359" />

          <span className="relative z-10 mb-4 text-[11px] font-semibold uppercase tracking-[4px] text-neutral-400" data-id="element-360">
            Who's it for?
          </span>

          <div role="tablist" aria-label="Choose how you use Giga" className="relative z-10 grid grid-cols-2 gap-1 p-2 w-full max-w-md rounded-full border border-white/70 bg-white/50 backdrop-blur-xl shadow-[0_12px_44px_rgba(43,33,26,0.12),inset_0_1px_0_rgba(255,255,255,0.8)]" data-id="element-361">
            {/* Liquid-glass sliding indicator — fully rounded, glossy, with a
             soft top sheen and an inner highlight so it reads like a pane of
             lit glass rather than a flat pill. */}
            <motion.div aria-hidden="true" className="absolute inset-y-2 w-[calc(50%-10px)] overflow-hidden rounded-full bg-[#2B211A]/95 shadow-[0_6px_18px_rgba(43,33,26,0.28),inset_0_1px_0_rgba(255,255,255,0.25)]" animate={{
            left: audience === 'personal' ? '8px' : 'calc(50% + 2px)'
          }} transition={{
            type: 'spring',
            stiffness: 380,
            damping: 34
          }} data-id="element-362">
              <span className="pointer-events-none absolute inset-x-0 top-0 h-1/2 rounded-t-full bg-gradient-to-b from-white/25 to-transparent" data-id="element-363" />
              <span className="pointer-events-none absolute -left-1/3 top-0 h-full w-1/2 -skew-x-12 bg-white/10 blur-md" data-id="element-364" />
            </motion.div>
            <button role="tab" aria-selected={audience === 'personal'} onClick={() => pick('personal')} onFocus={() => setPaused(true)} className={`group relative z-10 flex items-center justify-center gap-2.5 px-5 md:px-7 py-3 rounded-full text-sm md:text-[15px] font-semibold transition-colors duration-300 ${audience === 'personal' ? 'text-white' : 'text-neutral-500 hover:text-[#2B211A]'}`} data-id="element-365">
              <span className={`flex items-center justify-center w-8 h-8 rounded-full border transition-all duration-300 ${audience === 'personal' ? 'bg-[#EA580C] border-[#EA580C]/40 text-white shadow-[0_2px_10px_rgba(234,88,12,0.5),inset_0_1px_0_rgba(255,255,255,0.4)] scale-100' : 'bg-white/50 border-white/70 text-neutral-400 shadow-sm scale-95 group-hover:scale-100'}`} data-id="element-366">
                <UserRound size={16} strokeWidth={2.2} data-id="element-367" />
              </span>
              <span className="whitespace-nowrap" data-id="element-368">For you</span>
            </button>
            <button role="tab" aria-selected={audience === 'team'} onClick={() => pick('team')} onFocus={() => setPaused(true)} className={`group relative z-10 flex items-center justify-center gap-2.5 px-5 md:px-7 py-3 rounded-full text-sm md:text-[15px] font-semibold transition-colors duration-300 ${audience === 'team' ? 'text-white' : 'text-neutral-500 hover:text-[#2B211A]'}`} data-id="element-369">
              <span className={`flex items-center justify-center w-8 h-8 rounded-full border transition-all duration-300 ${audience === 'team' ? 'bg-[#EA580C] border-[#EA580C]/40 text-white shadow-[0_2px_10px_rgba(234,88,12,0.5),inset_0_1px_0_rgba(255,255,255,0.4)] scale-100' : 'bg-white/50 border-white/70 text-neutral-400 shadow-sm scale-95 group-hover:scale-100'}`} data-id="element-370">
                <UsersRound size={16} strokeWidth={2.2} data-id="element-371" />
              </span>
              <span className="whitespace-nowrap" data-id="element-372">For your team</span>
            </button>
          </div>
        </motion.div>

        {/* Swappable Content */}
        <div className="relative min-h-[400px]" data-id="element-373">
          <AnimatePresence mode="wait" data-id="element-374">
            {audience === 'personal' ? <motion.div key="personal" initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -20
          }} transition={{
            duration: 0.4,
            ease: 'easeOut'
          }} className="flex flex-col items-stretch" data-id="element-375">
                <div className="text-center max-w-2xl mx-auto mb-12 md:mb-14" data-id="element-376">
                  <h3 className="text-3xl md:text-4xl font-title font-medium text-[#2B211A] tracking-tight leading-[1.15] mb-4" data-id="element-377">
                    Giga does the work you{' '}
                    <RotatingPhrase phrases={ENDING_PHRASES.personal} data-id="element-378" />
                  </h3>
                  <p className="text-base md:text-lg text-neutral-500 leading-relaxed" data-id="element-379">
                    Follow-ups, admin, scheduling, and prep. Giga handles it so
                    your hours go to the decisions only you can make.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 w-full max-w-5xl" data-id="element-380">
                  <FeatureCard index={0} visualType="connectors" title="Inbox triaged by 9am" description="Email sorted and replies drafted for your sign-off before you sit down." data-id="element-381" />
                  <FeatureCard index={1} visualType="operates" title="Investor updates drafted" description="Giga pulls the numbers and writes the monthly update in your voice, ready to send." data-id="element-382" />
                  <FeatureCard index={2} visualType="learns" title="Follow-ups tracked" description="Every promise you make in a meeting becomes a task Giga chases to done." data-id="element-383" />
                </div>
              </motion.div> : <motion.div key="team" initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -20
          }} transition={{
            duration: 0.4,
            ease: 'easeOut'
          }} className="flex flex-col items-stretch" data-id="element-384">
                <div className="text-left max-w-2xl mb-12 md:mb-14" data-id="element-385">
                  <h3 className="text-3xl md:text-4xl font-title font-medium text-[#2B211A] tracking-tight leading-[1.15] mb-4" data-id="element-386">
                    Giga does the work you{' '}
                    <RotatingPhrase phrases={ENDING_PHRASES.team} data-id="element-387" />
                  </h3>
                  <p className="text-base md:text-lg text-neutral-500 leading-relaxed" data-id="element-388">
                    The work that never adds up to a full role gets done, so a
                    small team covers the ground of a larger one.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 w-full max-w-5xl" data-id="element-389">
                  <FeatureCard index={0} visualType="map" title="Backlog cleared" description="Reports, status updates, and reconciliations get handled before they pile up." data-id="element-390" />
                  <FeatureCard index={1} visualType="permissions" title="New hires ramp faster" description="Giga already knows your tools and process, so people ship work in week one." data-id="element-391" />
                  <FeatureCard index={2} visualType="dissemination" title="One method, whole team" description="When one person finds a faster way, everyone runs it the next day." data-id="element-392" />
                </div>
              </motion.div>}
          </AnimatePresence>
        </div>
      </div>
    </section>;
}
function PremiumVisual({
  type
}: {
  type: string;
}) {
  // A light, warm visual stage that matches the cream brand instead of a dark
  // box. Subtle dotted grid + a soft orange aura give depth without clashing.
  return <div className="relative w-full h-44 overflow-hidden border-b border-neutral-200/70 bg-[#FBF9F5] group-hover:bg-[#FAF7F1] transition-colors duration-500" data-id="element-393">
      {/* Faint dotted grid for texture */}
      <div className="absolute inset-0 bg-[radial-gradient(rgba(43,33,26,0.05)_1px,transparent_1px)] [background-size:14px_14px]" data-id="element-394" />
      {/* Soft warm aura, top-right */}
      <div className="pointer-events-none absolute -top-10 -right-10 w-44 h-44 rounded-full bg-[radial-gradient(circle,rgba(234,88,12,0.12),transparent_70%)]" data-id="element-395" />

      {/* Foreground minimalistic element based on type */}
      <div className="absolute inset-0 flex items-center justify-center" data-id="element-396">
        {type === 'connectors' && <div className="relative flex items-center justify-center w-full h-full" data-id="element-397">
            {/* Central Giga hub */}
            <div className="relative z-10 w-12 h-12 rounded-xl bg-[#EA580C] flex items-center justify-center shadow-[0_0_24px_rgba(234,88,12,0.45)]" data-id="element-398">
              <span className="text-white font-bold text-sm" data-id="element-399">G</span>
            </div>
            {/* Named tool nodes wiring into the hub */}
            {[{
          slug: 'slack',
          x: '14%',
          y: '24%',
          d: 0
        }, {
          slug: 'stripe',
          x: '84%',
          y: '28%',
          d: 0.4
        }, {
          slug: 'notion',
          x: '20%',
          y: '76%',
          d: 0.8
        }, {
          slug: 'salesforce',
          x: '82%',
          y: '74%',
          d: 1.2
        }].map(node => <Fragment key={node.slug} data-id="element-400">
                {/* Connecting line with a pulse traveling toward the hub */}
                <svg className="absolute inset-0 w-full h-full pointer-events-none" data-id="element-401">
                  <line x1={node.x} y1={node.y} x2="50%" y2="50%" stroke="rgba(234,88,12,0.35)" strokeWidth="1" strokeDasharray="3 3" data-id="element-402" />
                </svg>
                <div className="absolute -translate-x-1/2 -translate-y-1/2 w-8 h-8 rounded-lg bg-white border border-neutral-200/80 flex items-center justify-center shadow-sm" style={{
            left: node.x,
            top: node.y
          }} data-id="element-403">
                  <img src={`https://cdn.simpleicons.org/${node.slug}`} alt="" aria-hidden="true" className="w-4 h-4 object-contain" data-id="element-404" />
                </div>
              </Fragment>)}
          </div>}

        {type === 'operates' && <div className="w-48 rounded-xl bg-white border border-neutral-200/80 p-3 shadow-sm" data-id="element-405">
            {[{
          label: 'Open browser session',
          delay: 0
        }, {
          label: 'Query the database',
          delay: 0.8
        }, {
          label: 'Update the record',
          delay: 1.6
        }].map((step, i) => <div key={step.label} className="flex items-center gap-2 py-1" data-id="element-406">
                <motion.span className="flex items-center justify-center w-4 h-4 rounded-full border border-[#EA580C]/60 text-[#EA580C]" initial={{
            opacity: 0.25
          }} animate={{
            opacity: [0.25, 1, 1]
          }} transition={{
            duration: 3,
            repeat: Infinity,
            times: [0, 0.3, 1],
            delay: step.delay
          }} data-id="element-407">
                  <CheckCircle2 size={11} strokeWidth={3} data-id="element-408" />
                </motion.span>
                <motion.span className="text-[10px] font-mono text-[#6B5D52] whitespace-nowrap" initial={{
            opacity: 0.35
          }} animate={{
            opacity: [0.35, 1, 1]
          }} transition={{
            duration: 3,
            repeat: Infinity,
            times: [0, 0.3, 1],
            delay: step.delay
          }} data-id="element-409">
                  {step.label}
                </motion.span>
              </div>)}
          </div>}

        {type === 'learns' && <div className="relative flex items-center justify-center w-full h-full" data-id="element-410">
            {/* Memory core */}
            <motion.div className="relative z-10 w-12 h-12 rounded-full bg-[#EA580C]/15 border border-[#EA580C]/50 flex items-center justify-center shadow-[0_0_24px_rgba(234,88,12,0.25)]" animate={{
          scale: [1, 1.08, 1]
        }} transition={{
          duration: 3,
          repeat: Infinity,
          ease: 'easeInOut'
        }} data-id="element-411">
              <Sparkles size={20} className="text-[#EA580C]" data-id="element-412" />
            </motion.div>
            {/* Context chips being absorbed into the core */}
            {[{
          label: 'tools',
          x: '12%',
          y: '30%',
          d: 0
        }, {
          label: 'data',
          x: '80%',
          y: '24%',
          d: 0.7
        }, {
          label: 'prefs',
          x: '78%',
          y: '72%',
          d: 1.4
        }].map(chip => <motion.span key={chip.label} className="absolute -translate-x-1/2 -translate-y-1/2 px-2 py-0.5 rounded-full bg-white/90 text-[9px] font-mono font-semibold text-[#2B211A]" style={{
          left: chip.x,
          top: chip.y
        }} animate={{
          left: [chip.x, '50%'],
          top: [chip.y, '50%'],
          opacity: [1, 0],
          scale: [1, 0.6]
        }} transition={{
          duration: 2.4,
          repeat: Infinity,
          delay: chip.d,
          ease: 'easeIn'
        }} data-id="element-413">
                {chip.label}
              </motion.span>)}
          </div>}

        {type === 'map' && <div className="relative w-full h-full flex items-center justify-center" style={{
        transform: 'rotateX(60deg) rotateZ(45deg) scale(1.2)'
      }} data-id="element-414">
            <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(43,33,26,0.06)_1px,transparent_1px),linear-gradient(to_bottom,rgba(43,33,26,0.06)_1px,transparent_1px)] bg-[size:20px_20px]" data-id="element-415" />
            <motion.div className="absolute w-4 h-4 bg-[#EA580C] rounded-full shadow-[0_0_20px_#EA580C]" animate={{
          scale: [1, 1.2, 1]
        }} transition={{
          duration: 2,
          repeat: Infinity
        }} data-id="element-416" />
            <motion.div className="absolute w-2 h-2 bg-[#2B211A]/70 rounded-full" style={{
          top: '30%',
          left: '30%'
        }} animate={{
          opacity: [0.2, 1, 0.2]
        }} transition={{
          duration: 3,
          repeat: Infinity
        }} data-id="element-417" />
            <motion.div className="absolute w-2 h-2 bg-[#2B211A]/70 rounded-full" style={{
          top: '70%',
          left: '70%'
        }} animate={{
          opacity: [0.2, 1, 0.2]
        }} transition={{
          duration: 3,
          repeat: Infinity,
          delay: 1
        }} data-id="element-418" />
            <svg className="absolute inset-0 w-full h-full" data-id="element-419">
              <motion.line x1="50%" y1="50%" x2="30%" y2="30%" stroke="#EA580C" strokeWidth="1" strokeOpacity="0.5" initial={{
            pathLength: 0
          }} animate={{
            pathLength: 1
          }} transition={{
            duration: 2,
            repeat: Infinity
          }} data-id="element-420" />
              <motion.line x1="50%" y1="50%" x2="70%" y2="70%" stroke="#EA580C" strokeWidth="1" strokeOpacity="0.5" initial={{
            pathLength: 0
          }} animate={{
            pathLength: 1
          }} transition={{
            duration: 2,
            repeat: Infinity,
            delay: 0.5
          }} data-id="element-421" />
            </svg>
          </div>}

        {type === 'permissions' && <div className="relative flex items-center justify-center" data-id="element-422">
            <ShieldCheck size={48} strokeWidth={1} className="text-[#2B211A]/20" data-id="element-423" />
            <motion.div className="absolute inset-0 overflow-hidden" style={{
          clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)'
        }} data-id="element-424">
              <motion.div className="w-full h-[2px] bg-[#EA580C] shadow-[0_0_15px_#EA580C]" animate={{
            y: [-10, 60, -10]
          }} transition={{
            duration: 3,
            repeat: Infinity,
            ease: 'linear'
          }} data-id="element-425" />
            </motion.div>
            <ShieldCheck size={48} strokeWidth={1} className="text-[#EA580C] absolute inset-0 opacity-60" data-id="element-426" />
          </div>}

        {type === 'dissemination' && <div className="relative flex items-center justify-center" data-id="element-427">
            <motion.div className="absolute w-8 h-8 bg-[#EA580C] rounded-full blur-xl" animate={{
          scale: [1, 1.5, 1],
          opacity: [0.3, 0.6, 0.3]
        }} transition={{
          duration: 2,
          repeat: Infinity
        }} data-id="element-428" />
            <div className="w-7 h-7 bg-white rounded-lg shadow-md border border-neutral-200/80 z-10 flex items-center justify-center" data-id="element-429">
              <div className="w-3 h-[2px] bg-[#EA580C]/60 rounded-full" data-id="element-430" />
            </div>
            {[0, 1, 2].map(i => <motion.div key={i} className="absolute w-full h-full border border-[#EA580C]/40 rounded-full" initial={{
          scale: 0.5,
          opacity: 1
        }} animate={{
          scale: 3,
          opacity: 0
        }} transition={{
          duration: 2,
          repeat: Infinity,
          delay: i * 0.6,
          ease: 'easeOut'
        }} data-id="element-431" />)}
          </div>}
      </div>
    </div>;
}
function FeatureCard({
  visualType,
  title,
  description,
  index = 0
}: {
  visualType: string;
  title: string;
  description: string;
  index?: number;
}) {
  return <motion.div initial={{
    opacity: 0,
    y: 18
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-60px'
  }} transition={{
    duration: 0.5,
    delay: index * 0.08,
    ease: [0.16, 1, 0.3, 1]
  }} className="group relative flex flex-col rounded-[2rem] bg-white border border-neutral-200/70 shadow-[0_4px_24px_rgba(43,33,26,0.05)] hover:shadow-[0_28px_70px_rgba(234,88,12,0.14)] hover:-translate-y-1.5 hover:border-[#EA580C]/35 transition-all duration-500 overflow-hidden" data-id="element-432">
      {/* Hover glow bloom — a warm halo that rises behind the card on hover so
              each card feels lit by the same stage as the rest of the section. */}
      <div aria-hidden="true" className="pointer-events-none absolute -inset-px rounded-[2rem] opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-[radial-gradient(120%_80%_at_50%_0%,rgba(234,88,12,0.10),transparent_60%)]" data-id="element-433" />
      {/* Top highlight sheen */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white to-transparent" data-id="element-434" />

      <div className="relative" data-id="element-435">
        <PremiumVisual type={visualType} data-id="element-436" />
        {/* Numbered badge floating over the visual stage. */}
        <span className="absolute left-5 top-5 flex h-7 w-7 items-center justify-center rounded-full bg-white/90 border border-neutral-200/80 text-[12px] font-bold text-[#EA580C] shadow-sm backdrop-blur-sm" data-id="element-437">
          {index + 1}
        </span>
      </div>

      <div className="relative p-6 md:p-8 flex flex-col flex-1" data-id="element-438">
        <h3 className="text-lg md:text-xl font-title font-semibold text-[#2B211A] mb-2.5 group-hover:text-[#EA580C] transition-colors duration-300" data-id="element-439">
          {title}
        </h3>
        <p className="text-neutral-500 leading-relaxed text-sm md:text-[15px]" data-id="element-440">
          {description}
        </p>
      </div>
    </motion.div>;
}